import { ColDef, ColGroupDef, GridOptions, RowNode } from 'ag-grid';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PaystubDAO } from '../../../core/services/dao/paystubDAO';
import { ActivatedRoute, Router } from '@angular/router';
import { EditButtonComponent } from './editButton/editButton.component';
import { DownloadButtonComponent } from './downloadButton/downloadButton.component';
import { FileOpenButtonComponent } from './fileOpenButton/fileOpenButton.component';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { viewEditExists } from '../../../core/pure-functions/grids/grid-column-helper/grid-column-helper';
import { Observable } from 'rxjs/internal/Observable';
import { UploadDAO } from '../../../core/services/dao/uploadDAO';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'list-grid',
  templateUrl: './listGrid.component.html',
  styleUrls: ['./listGrid.component.scss'],
})
export class ListGridComponent
  implements OnInit, OnChanges, ContainsSubscriptions, OnDestroy
{
  private activeMediaQuery = '';
  public gridOptions: GridOptions;
  @Input()
  columnDefs: (ColDef | ColGroupDef)[];
  @Input()
  rowData: any[] = [];
  @Input()
  rowHeight: number = 48;
  @Input()
  searchToolBar: boolean;
  @Input()
  manageButton: boolean;
  @Input()
  editPath: string;
  @Input()
  viewPath: string;
  @Input()
  headerHeight: number;
  @Input()
  externalFilterFunc: (node: RowNode) => boolean;
  @Input()
  allowExport: boolean;
  @Input()
  exportParams: any;
  @Input()
  hideEditButton: boolean;
  @Input()
  showDownloadButton: boolean;
  @Input()
  openPaystub: boolean = false;
  @Input()
  openCharterAgreement: boolean = false;
  @Input()
  userIdForEdit: boolean;
  @Input()
  listGridAsyncValidator: ListGridAsyncValidator;
  @Input()
  listGridAsyncData: BehaviorSubject<ListGridAsyncData>;
  @Input()
  autoAdjustListGridHeight: boolean = false;
  @Input()
  customId: string;
  @Input()
  pageResultCount: number;
  @Input()
  searchOptions = {};
  @Output()
  action: EventEmitter<any> = new EventEmitter<any>();
  listGridHeight: string = '70vh';

  subs: Subscription[] = [];

  private searchTimeout: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public media: MediaObserver,
    private paystubDAO: PaystubDAO,
    private uploadDao: UploadDAO,
    private snackBar: MatSnackBar,
  ) {
    const watcherSub = media
      .asObservable()
      .subscribe((change: MediaChange[]) => {
        this.activeMediaQuery = change
          ? `'${change[0].mqAlias}' = (${change[0].mediaQuery})`
          : '';
        this.resizeGrid();
      });
    this.subs.push(watcherSub);
    this.searchToolBar = true;
  }

  adjustListGridHeight() {
    if (this.rowData.length) {
      this.listGridHeight = `${this.rowData.length * this.rowHeight + 33}px`;
    } else {
      this.listGridHeight = '200px';
    }
  }

  resizeGrid() {
    if (this.gridOptions) {
      this.gridOptions.api?.sizeColumnsToFit(); // attempt resize at media breakpoints
    }
  }

  getRowStyle(params) {
    if (params.node.rowIndex % 2 !== 0) {
      return { background: '#ecf0f1' };
    }
  }

  hasManageButton() {
    return this.manageButton;
  }

  ngOnInit() {
    this.gridOptions = <GridOptions>{};
    this.gridOptions.rowHeight = this.rowHeight;
    this.gridOptions.groupSuppressAutoColumn = true;
    this.gridOptions.enableSorting = true;

    /* set minimum column widths if not defined */
    this.columnDefs.forEach(function (columnDef: ColDef) {
      if (!columnDef.minWidth) {
        columnDef.minWidth = 120;
      }
    });

    this.gridOptions.columnDefs = this.columnDefs;

    if (!this.hideEditButton && !viewEditExists(this.gridOptions.columnDefs)) {
      this.gridOptions.columnDefs.push({
        headerName: 'View/Edit',
        suppressMenu: true,
        cellRendererFramework: EditButtonComponent,
        suppressSizeToFit: true,
        width: 100,
        pinned: 'right',
      });
    }

    if (this.showDownloadButton) {
      this.gridOptions.columnDefs.push({
        headerName: 'View',
        suppressMenu: true,
        cellRendererFramework: DownloadButtonComponent,
        suppressSizeToFit: true,
        width: 150,
      });
      this.gridOptions.columnDefs.unshift({
        headerName: 'Filename',
        suppressMenu: true,
        cellRendererFramework: FileOpenButtonComponent,
        suppressSizeToFit: true,
        width: 700,
      });
    }

    this.gridOptions.rowData = this.rowData;
    this.gridOptions.enableFilter = true;
    this.gridOptions.suppressScrollOnNewData = true;

    this.gridOptions.onGridReady = () => {
      this.gridOptions.api.sizeColumnsToFit();
      if (this.externalFilterFunc) {
        // if filter func is defined
        this.gridOptions.isExternalFilterPresent = this.useExternalFilter; // set use external filter to true
        this.gridOptions.doesExternalFilterPass = this.externalFilterFunc; // assign filter function
      }
      this.gridOptions.api.onFilterChanged(); // filter grid
    };
    this.gridOptions.icons = {
      checkboxChecked:
        '<img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAOCAYAAAAfSC3RAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA2ZpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0UmVmPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VSZWYjIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0ieG1wLmRpZDoxMTQzMkY1NDIyMjhFNjExQkVGOEFCQUI5MzdBNjFEMSIgeG1wTU06RG9jdW1lbnRJRD0ieG1wLmRpZDoyMzBBQkU2ODI4MjQxMUU2QjlDRUZCNUFDREJGRTVDMCIgeG1wTU06SW5zdGFuY2VJRD0ieG1wLmlpZDoyMzBBQkU2NzI4MjQxMUU2QjlDRUZCNUFDREJGRTVDMCIgeG1wOkNyZWF0b3JUb29sPSJBZG9iZSBQaG90b3Nob3AgQ1M2IChXaW5kb3dzKSI+IDx4bXBNTTpEZXJpdmVkRnJvbSBzdFJlZjppbnN0YW5jZUlEPSJ4bXAuaWlkOjE0NDMyRjU0MjIyOEU2MTFCRUY4QUJBQjkzN0E2MUQxIiBzdFJlZjpkb2N1bWVudElEPSJ4bXAuZGlkOjExNDMyRjU0MjIyOEU2MTFCRUY4QUJBQjkzN0E2MUQxIi8+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+O+zv0gAAAQ1JREFUeNpilJvw35OBgWEuEEsyEAeeA3EyI1DjMxI0wTUzkaEJBCSZiFVpJcvAsDqEgUFVCMInSqOeOAPDLG8GBjNpBoZCCyI1KggwMCzwZ2DgZWdgOPWUgaF4F5pGDxWgqT4MDPzsSB7hYWBYHMDAIMzJwHDjDQND0mYGhu9/0DT6qTEwuCszMOyIZmAwkoTYALJJjp+B4cEHBoaEjQwMn38iDAVFx38wA4gzTBgYSiwhEi++MDDI8DEwvP3OwBC0CqIZGcBtBOmefoaBIXQNA8PvfxBNf4B03AZMTVgD5xwwXcQDFX/8wcAw+RQDw5VX2AMN7lRSARM07ZEKXoA0poAYJGh6CkrkAAEGAKNeQxaS7i+xAAAAAElFTkSuQmCC"/>',
    };
    this.gridOptions.context = {
      componentParent: this,
    };
    this.gridOptions.enableColResize = true;
    if (this.autoAdjustListGridHeight) {
      this.adjustListGridHeight();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.rowData && this.gridOptions) {
      this.gridOptions.api.setRowData(changes.rowData.currentValue);
    }
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  public onActionViewClick(data: any) {
    const path = this.editPath;
    if (this.userIdForEdit) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([path, data.user.id]),
      );
      window.open(url, '_blank');
    } else {
      const id = data[this.customId] || data.id;
      const url = this.router.serializeUrl(
        this.router.createUrlTree([path, id]),
      );
      window.open(url, '_blank');
    }
  }

  public onActionApproveClick(data: any) {
    this.action.emit({ type: 'approve', data: data });
  }

  public onActionRejectClick(data: any) {
    this.action.emit({ type: 'reject', data: data });
  }

  public onActivateDeactivateClick(data: any) {
    this.action.emit({ type: 'activateDeactivate', data: data });
  }

  private useExternalFilter() {
    return true;
  }

  public onActionRemoveClick(data: any) {}

  public onQuickFilterChanged($event) {
    if (this.listGridAsyncValidator) {
      const searchQuery = $event.target.value.replace('+', '%2B');
      this.listGridAsyncData.next({
        ...this.listGridAsyncData.value,
        searchQuery: searchQuery,
      });
      let sortModel = this.gridOptions.api.getSortModel();
      if (sortModel && sortModel.length && searchQuery.length > 3) {
        this.sortIfAsync();
      } else if (searchQuery.length > 3) {
        this.debounceSearch(0, searchQuery);
      } else if (!searchQuery.length) {
        this.debounceSearch(0);
      }
    } else {
      this.gridOptions.api.setQuickFilter($event.target.value);
    }
  }

  public exportToCSV() {
    this.gridOptions.api.exportDataAsCsv(this.exportParams);
  }

  public onActionGetPDF(id) {
    let windowReference = window.open();
    if (this.openPaystub) {
      const sub = this.paystubDAO.getPaystubsPDF(id).subscribe((pdf) => {
        windowReference.location = pdf.signedRequest;
      });
      this.subs.push(sub);
    } else if (this.openCharterAgreement) {
      const sub = this.uploadDao.downloadFileWithKey(id).subscribe((URL) => {
        if (URL && URL.signedRequest) {
          windowReference.location = URL.signedRequest;
        } else {
          this.snackBar.open(
            'Error with obtaining signed URL for archived charter agreement.',
            null,
            {
              duration: 5000,
              verticalPosition: 'top',
            },
          );
        }
      });
      this.subs.push(sub);
    }
  }

  public onActionOpenPDF(id) {
    if (this.openPaystub) {
      const sub = this.paystubDAO.getPaystubsPDF(id).subscribe((pdf) => {
        window.open(pdf.signedRequest);
      });
      this.subs.push(sub);
    } else if (this.openCharterAgreement) {
      const sub = this.uploadDao.downloadFileWithKey(id).subscribe((URL) => {
        if (URL && URL.signedRequest) {
          window.open(URL.signedRequest);
        } else {
          this.snackBar.open(
            'Error with obtaining signed URL for archived charter agreement.',
            null,
            {
              duration: 5000,
              verticalPosition: 'top',
            },
          );
        }
      });
      this.subs.push(sub);
    }
  }

  public emitAction(type, { data }) {
    this.action.emit({ type, data });
  }

  public editRate(rate) {
    this.action.emit({ type: 'editRate', data: rate.data });
  }

  deleteRate(rate) {
    this.action.emit({ type: 'deleteRate', data: rate.data });
  }

  public openSplitRate(rate) {
    this.action.emit({ type: 'openSplitRate', data: rate.data });
  }

  private debounceSearch(
    page: number,
    query?: string,
    sortBy?: string,
    sortOrder?: string,
  ) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.gridOptions.api.showLoadingOverlay();
    this.searchTimeout = setTimeout(() => {
      const runSearchSub = this.listGridAsyncValidator
        .runSearch(page, query, sortBy, sortOrder, this.searchOptions)
        .subscribe((data) =>
          this.listGridAsyncData.next({
            page: page,
            numberOfPages: Math.floor(
              data.count / (this.pageResultCount || 50),
            ),
            rows: data.rows,
            count: data.count,
            searchQuery: query,
            sortBy: sortBy,
            sortOrder: sortOrder,
          }),
        );
      this.gridOptions.api.hideOverlay();
      this.subs.push(runSearchSub);
    }, 1000);
  }

  sortIfAsync() {
    if (this.listGridAsyncValidator) {
      let sortModel = this.gridOptions.api.getSortModel();
      if (sortModel && sortModel.length) {
        const searchQuery = this.listGridAsyncData.value.searchQuery;
        this.debounceSearch(
          0,
          searchQuery,
          sortModel[0].colId,
          sortModel[0].sort.toUpperCase(),
        );
      }
    }
  }
}

export interface ListGridAsyncValidator {
  runSearch(
    page: number,
    query?: string,
    sortBy?: string,
    sortOrder?: string,
    options?: {},
  ): Observable<AsyncValidatorResult>;
}

export interface AsyncValidatorResult {
  count: number;
  rows: any[];
}

export interface ListGridAsyncData {
  count: number;
  page: number;
  rows: any[];
  searchQuery: string;
  numberOfPages: number;
  sortBy: string;
  sortOrder: string;
}
